import { ReactNode, useMemo } from 'react';
import { useTracking } from 'react-tracking';
import Tooltip from '@components/Tooltip';
import Description from './components/Description';
import { Container, SnowflakeSlice } from './styled';
import { SnowflakeIndices, Mark } from '@/pages/CompanyReport/redux/interface';
import type { AnalysisChecks } from '@/pages/CompanyReport/components/SummaryCompare';

type RenderProp = (score: number) => ReactNode;

interface Props {
  onClick?: (index: SnowflakeIndices) => void;
  hasHoverInfo?: boolean;
  points: number[];
  anchors: string[];
  descriptions: string[];
  detailedPoints?: AnalysisChecks;
  children?: RenderProp;
  selectorPrefix?: string;
}

const SnowflakeTooltip = ({
  points,
  anchors,
  descriptions,
  onClick,
  hasHoverInfo,
  detailedPoints,
  children,
  selectorPrefix,
}: Props) => {
  const { trackEvent } = useTracking();
  const descriptionOrNull = (description: ReactNode) => {
    if (hasHoverInfo) {
      return description;
    }
    return null;
  };

  const trackHover = (trackingProp: string) => () => {
    trackEvent({
      subject: 'snowflake',
      action: 'hover',
      properties: trackingProp,
    });
  };

  const clickHandler =
    (trackingProp: string, index: SnowflakeIndices) => () => {
      if (onClick) {
        onClick(index);
        trackEvent({
          subject: 'snowflake',
          action: 'click',
          properties: trackingProp,
        });
      }
    };

  const getDetailedScore = (index: SnowflakeIndices): Mark[] => {
    if (detailedPoints) {
      return detailedPoints[index];
    }
    return ['FAIL', 'FAIL', 'FAIL', 'FAIL', 'FAIL', 'FAIL'];
  };

  const computedSelectorPrefix = useMemo(() => {
    if (typeof selectorPrefix === 'string') {
      return `${selectorPrefix}-`;
    }
    return '';
  }, [selectorPrefix]);
  return (
    <Container>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 228 228"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <Tooltip
          title={descriptionOrNull(
            <Description
              heading="Valuation"
              description={descriptions[SnowflakeIndices.VALUE]}
              anchor={anchors[SnowflakeIndices.VALUE]}
              score={points[SnowflakeIndices.VALUE]}
              detailedScore={getDetailedScore(SnowflakeIndices.VALUE)}
            >
              {children}
            </Description>
          )}
          onOpen={trackHover('value')}
          placement="top"
        >
          <SnowflakeSlice
            data-cy-id={`${computedSelectorPrefix}snowflake-slice-value`}
            onClick={clickHandler('value', SnowflakeIndices.VALUE)}
            d="M46.9925 21.772C66.4691 7.62147 89.9255 1.11925e-05 114 0C138.074 -1.11925e-05 161.531 7.62143 181.007 21.772L114 114L46.9925 21.772Z"
          />
        </Tooltip>
        <Tooltip
          title={descriptionOrNull(
            <Description
              heading="Future Growth"
              description={descriptions[SnowflakeIndices.FUTURE]}
              anchor={anchors[SnowflakeIndices.FUTURE]}
              score={points[SnowflakeIndices.FUTURE]}
              detailedScore={getDetailedScore(SnowflakeIndices.FUTURE)}
            >
              {children}
            </Description>
          )}
          onOpen={trackHover('future')}
          placement="top"
        >
          <SnowflakeSlice
            data-cy-id={`${computedSelectorPrefix}snowflake-slice-future`}
            onClick={clickHandler('future', SnowflakeIndices.FUTURE)}
            d="M181.008 21.7721C200.484 35.9227 214.981 55.8759 222.42 78.7721C229.86 101.668 229.86 126.332 222.42 149.228L114 114L181.008 21.7721Z"
          />
        </Tooltip>
        <Tooltip
          title={descriptionOrNull(
            <Description
              heading="Past Performance"
              description={descriptions[SnowflakeIndices.PAST]}
              anchor={anchors[SnowflakeIndices.PAST]}
              score={points[SnowflakeIndices.PAST]}
              detailedScore={getDetailedScore(SnowflakeIndices.PAST)}
            >
              {children}
            </Description>
          )}
          onOpen={trackHover('past')}
          placement="top"
        >
          <SnowflakeSlice
            data-cy-id={`${computedSelectorPrefix}snowflake-slice-past`}
            onClick={clickHandler('past', SnowflakeIndices.PAST)}
            d="M222.42 149.228C214.981 172.124 200.484 192.077 181.008 206.228C161.531 220.379 138.074 228 114 228L114 114L222.42 149.228Z"
          />
        </Tooltip>
        <Tooltip
          title={descriptionOrNull(
            <Description
              heading="Financial Health"
              description={descriptions[SnowflakeIndices.HEALTH]}
              anchor={anchors[SnowflakeIndices.HEALTH]}
              score={points[SnowflakeIndices.HEALTH]}
              detailedScore={getDetailedScore(SnowflakeIndices.HEALTH)}
            >
              {children}
            </Description>
          )}
          onOpen={trackHover('health')}
          placement="top"
        >
          <SnowflakeSlice
            data-cy-id={`${computedSelectorPrefix}snowflake-slice-health`}
            onClick={clickHandler('health', SnowflakeIndices.HEALTH)}
            d="M114 228C89.9256 228 66.4691 220.379 46.9925 206.228C27.5158 192.077 13.019 172.124 5.57955 149.228L114 114L114 228Z"
          />
        </Tooltip>
        <Tooltip
          title={descriptionOrNull(
            <Description
              heading="Dividend"
              description={descriptions[SnowflakeIndices.DIVIDEND]}
              anchor={anchors[SnowflakeIndices.DIVIDEND]}
              score={points[SnowflakeIndices.DIVIDEND]}
              detailedScore={getDetailedScore(SnowflakeIndices.DIVIDEND)}
            >
              {children}
            </Description>
          )}
          onOpen={trackHover('dividend')}
          placement="top"
        >
          <SnowflakeSlice
            data-cy-id={`${computedSelectorPrefix}snowflake-slice-dividend`}
            onClick={clickHandler('dividend', SnowflakeIndices.DIVIDEND)}
            d="M5.57956 149.228C-1.85985 126.332 -1.85985 101.668 5.57956 78.7721C13.019 55.8759 27.5159 35.9226 46.9925 21.772L114 114L5.57956 149.228Z"
          />
        </Tooltip>
      </svg>
    </Container>
  );
};

SnowflakeTooltip.defaultProps = {
  hasHoverInfo: true,
};
export default SnowflakeTooltip;
